/**
 * 교시 종소리 설정 카드 (운영자) — 2026-06-30
 * 시설 정보 페이지. ON/OFF·볼륨·종소리 PC 주소 + 종 스케줄(자유목록) 편집 + 교시 자동채우기.
 * 백엔드: /api/v1/owner/bell/*
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

const authHeader = (): Record<string, string> => {
  const t = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const api = (p: string) => `${API_BASE_URL}/api/v1/owner/bell${p}`;

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const SOUNDS: { key: string; label: string }[] = [
  { key: 'bell1', label: '시작종' }, { key: 'bell2', label: '끝종' }, { key: 'bell3', label: '예비종' },
  { key: 'music1', label: '음악1' }, { key: 'music2', label: '음악2' },
];
const soundLabel = (k: string) => SOUNDS.find((s) => s.key === k)?.label || k;

interface Entry { id: string; ring_time: string; weekdays: number[]; sound: string; label: string | null; is_enabled: boolean; }

export default function BellCard() {
  const [token, setToken] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [volume, setVolume] = useState(100);
  const [schedule, setSchedule] = useState<Entry[]>([]);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // 새 항목 입력
  const [nt, setNt] = useState('09:00');
  const [nwd, setNwd] = useState<number[]>([1, 2, 3, 4, 5]);
  const [nsound, setNsound] = useState('bell1');
  const [nlabel, setNlabel] = useState('');

  const bellUrl = token ? `${window.location.origin}/bell/${token}` : '';

  // 미리듣기 재생/정지 토글
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const togglePreview = (key: string, sound: string) => {
    if (previewRef.current) { previewRef.current.pause(); previewRef.current.currentTime = 0; previewRef.current = null; }
    if (playingKey === key) { setPlayingKey(null); return; } // 같은 버튼 다시 누르면 정지
    const a = new Audio(`/bell-sounds/${sound}.mp3`);
    previewRef.current = a;
    setPlayingKey(key);
    a.onended = () => { setPlayingKey((k) => (k === key ? null : k)); previewRef.current = null; };
    a.play().catch(() => setPlayingKey(null));
  };

  const loadInfo = useCallback(async () => {
    const res = await fetch(api('/info'), { headers: authHeader() });
    if (res.ok) { const d = await res.json(); setToken(d.token || ''); setEnabled(!!d.is_enabled); setVolume(d.volume ?? 100); }
  }, []);
  const loadSchedule = useCallback(async () => {
    const res = await fetch(api('/schedule'), { headers: authHeader() });
    if (res.ok) setSchedule((await res.json()).schedule || []);
  }, []);
  useEffect(() => { loadInfo(); loadSchedule(); }, [loadInfo, loadSchedule]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const res = await fetch(api('/settings'), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ is_enabled: enabled, volume }) });
      if (res.ok) alert('종소리 설정이 저장되었습니다'); else alert('저장 실패');
    } finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm('종소리 주소를 재발급하면 기존 주소는 즉시 못 씁니다. 계속할까요?')) return;
    setBusy(true);
    try { const res = await fetch(api('/regenerate'), { method: 'POST', headers: authHeader() }); if (res.ok) { setToken((await res.json()).token || ''); alert('새 주소가 발급되었습니다'); } } finally { setBusy(false); }
  };
  const copyUrl = () => { if (bellUrl) { navigator.clipboard?.writeText(bellUrl); alert('주소가 복사되었습니다'); } };

  const addEntry = async () => {
    if (nwd.length === 0) { alert('요일을 1개 이상 선택하세요'); return; }
    setBusy(true);
    try {
      const res = await fetch(api('/schedule'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ ring_time: nt, weekdays: nwd, sound: nsound, label: nlabel || null }) });
      if (res.ok) { setNlabel(''); await loadSchedule(); } else alert((await res.json()).detail || '추가 실패');
    } finally { setBusy(false); }
  };
  const patchEntry = async (id: string, body: Record<string, unknown>) => {
    await fetch(api(`/schedule/${id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(body) });
    await loadSchedule();
  };
  const delEntry = async (id: string) => {
    if (!window.confirm('이 종을 삭제할까요?')) return;
    await fetch(api(`/schedule/${id}`), { method: 'DELETE', headers: authHeader() });
    await loadSchedule();
  };
  const autofill = async () => {
    const replace = window.confirm('교시 시간표에서 시작종·종료종을 자동으로 채웁니다.\n\n[확인] = 기존 종 목록을 지우고 새로 채움\n[취소] = 기존에 추가만 함');
    setBusy(true);
    try {
      const res = await fetch(api('/autofill'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ replace }) });
      const d = await res.json();
      alert(res.ok ? (d.message || '채웠습니다') : (d.detail || '실패'));
      if (res.ok) await loadSchedule();
    } finally { setBusy(false); }
  };

  // 선택 + 일괄 삭제
  const toggleSelect = (id: string) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = schedule.length > 0 && selectedIds.size === schedule.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(schedule.map((e) => e.id)));
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}개 종을 삭제할까요?`)) return;
    setBusy(true);
    try {
      await Promise.all([...selectedIds].map((id) => fetch(api(`/schedule/${id}`), { method: 'DELETE', headers: authHeader() })));
      setSelectedIds(new Set());
      await loadSchedule();
    } finally { setBusy(false); }
  };

  const box = 'bg-white shadow rounded-lg p-6 mb-6';
  const toggleWd = (arr: number[], set: (v: number[]) => void, w: number) => set(arr.includes(w) ? arr.filter((x) => x !== w) : [...arr, w].sort());

  return (
    <div className={box}>
      <h3 className="text-lg font-bold mb-1">🔔 교시 종소리</h3>
      <p className="text-gray-500 text-sm mb-4">독서실 PC 브라우저에서 아래 주소를 열어두면 설정한 시각에 종이 울립니다. (학교종 대체)</p>

      {/* 설정 */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> 종소리 켜기</label>
        <label className="flex items-center gap-2 text-sm">볼륨 <input type="range" min={0} max={100} value={volume} onChange={(e) => setVolume(Number(e.target.value))} /> {volume}</label>
        <button onClick={saveSettings} disabled={busy} className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold">설정 저장</button>
      </div>

      {/* 주소 */}
      <div className="flex gap-2 items-center flex-wrap mb-4">
        <input readOnly value={bellUrl} className="flex-1 min-w-[240px] border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700" />
        <button onClick={copyUrl} disabled={!bellUrl} className="px-3 py-2 rounded-lg border text-sm">복사</button>
        <a href={bellUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-blue-500 text-blue-600 text-sm">열기</a>
        <button onClick={regenerate} disabled={busy} className="px-3 py-2 rounded-lg border border-red-300 text-red-500 text-sm">재발급</button>
      </div>

      {/* 종 추가 */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">종 추가</h4>
          <button onClick={autofill} disabled={busy} className="px-3 py-1.5 rounded-lg border border-amber-500 text-amber-600 text-sm font-semibold">교시 시간표에서 자동채우기</button>
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-2">
          <input type="time" value={nt} onChange={(e) => setNt(e.target.value)} className="border rounded-lg px-2 py-1.5" />
          <select value={nsound} onChange={(e) => setNsound(e.target.value)} className="border rounded-lg px-2 py-1.5">
            {SOUNDS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button onClick={() => togglePreview('new', nsound)} className="px-2 py-1.5 rounded-lg border text-sm" title="미리듣기/정지">{playingKey === 'new' ? '⏹' : '▶'}</button>
          <input value={nlabel} onChange={(e) => setNlabel(e.target.value)} placeholder="메모 (예: 1교시 시작)" className="border rounded-lg px-2 py-1.5 flex-1 min-w-[140px]" />
        </div>
        <div className="flex gap-1 mb-2">
          {WD.map((w, i) => (
            <button key={i} onClick={() => toggleWd(nwd, setNwd, i)} className={`w-8 h-8 rounded-full text-xs font-semibold ${nwd.includes(i) ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{w}</button>
          ))}
          <button onClick={addEntry} disabled={busy} className="ml-auto bg-blue-600 text-white rounded-lg px-4 py-1.5 text-sm font-semibold">추가</button>
        </div>
      </div>

      {/* 종 목록 */}
      <div className="mt-3">
        {schedule.length === 0 ? <div className="text-gray-400 text-sm py-3">등록된 종이 없습니다 — 위에서 추가하거나 자동채우기 하세요</div> : (
          <>
            {/* 일괄 선택/삭제 바 */}
            <div className="flex items-center gap-3 mb-2 text-sm">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /> 전체 선택 ({schedule.length})</label>
              {selectedIds.size > 0 && (
                <button onClick={bulkDelete} disabled={busy} className="px-3 py-1 rounded-lg border border-red-400 text-red-600 font-semibold">선택 삭제 ({selectedIds.size})</button>
              )}
            </div>
            {/* 스크롤 박스 */}
            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
              {schedule.map((e) => (
                <div key={e.id} className={`flex items-center gap-2 px-2 py-2 text-sm flex-wrap ${selectedIds.has(e.id) ? 'bg-red-50' : ''}`}>
                  <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelect(e.id)} title="선택" />
                  <span className="font-bold w-12">{e.ring_time}</span>
                  <button onClick={() => togglePreview(e.id, e.sound)} className="text-gray-500" title="미리듣기/정지">{playingKey === e.id ? '⏹' : '▶'}</button>
                  <span className="text-gray-600 w-14">{soundLabel(e.sound)}</span>
                  <span className="text-gray-400 text-xs">{(e.weekdays || []).map((w) => WD[w]).join('')}</span>
                  <span className="flex-1 text-gray-700 min-w-[80px]">{e.label || ''}</span>
                  <label className="text-xs text-gray-500 flex items-center gap-1" title="이 종 사용 여부">
                    <input type="checkbox" checked={e.is_enabled} onChange={(ev) => patchEntry(e.id, { is_enabled: ev.target.checked })} /> 사용
                  </label>
                  <button onClick={() => delEntry(e.id)} className="px-2 py-1 rounded border border-red-300 text-red-500 text-xs">삭제</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-3">※ PC 절전(잠자기)을 끄고, 부팅 시 이 주소가 자동으로 열리게 해두면 무인으로 계속 울립니다.</p>
    </div>
  );
}
